import React, { useState } from "react";
import { BookOpen, X, Mic, Zap, Cpu, Gift, Keyboard } from "lucide-react";

interface DocsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = "pipeline" | "requisitos" | "costos" | "tips" | "minutas" | "atajos";

export function DocsModal({ isOpen, onClose }: DocsModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>("pipeline");

  if (!isOpen) return null;

  const tabs: { id: TabType; label: string }[] = [
    { id: "pipeline", label: "Pipeline" },
    { id: "requisitos", label: "Requisitos" },
    { id: "costos", label: "Costos & Créditos" },
    { id: "tips", label: "Tips de uso" },
    { id: "minutas", label: "Minutas y Correos" },
    { id: "atajos", label: "Atajos" },
  ];

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[var(--window-bg)] border border-[var(--border)] rounded-xl max-w-3xl w-full h-[680px] max-h-[88vh] shadow-2xl flex flex-col overflow-hidden animate-[modal-appear_0.22s_cubic-bezier(0.16,1,0.3,1)] text-[var(--text)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="docs-modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-[var(--surface)] border-b border-[var(--border)] flex-shrink-0">
          <div className="flex items-center gap-2">
            <BookOpen size={18} className="text-[var(--text-dim)]" />
            <h2 id="docs-modal-title" className="text-sm font-semibold text-[var(--text)] uppercase tracking-wider font-mono">
              Documentación Técnica
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="w-6 h-6 rounded-md bg-[var(--surface)] hover:bg-[var(--surface-hover)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] flex items-center justify-center transition-all"
            aria-label="Cerrar modal de documentación"
          >
            <X size={14} />
          </button>
        </div>

        {/* Tab Buttons */}
        <div className="flex border-b border-[var(--border)] bg-[var(--surface)] px-5 gap-2 overflow-x-auto scrollbar-none flex-shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-3.5 text-[13px] font-medium border-b-2 transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-[var(--accent)] text-[var(--accent)] font-semibold"
                  : "border-transparent text-[var(--text-dim)] hover:text-[var(--text-muted)]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Modal Body Content */}
        <div className="p-6 overflow-y-auto flex-1 text-[13.5px] text-[var(--text-muted)] leading-relaxed font-sans scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          {/* TAB: PIPELINE */}
          {activeTab === "pipeline" && (
            <div className="flex flex-col gap-6">
              <div>
                <h3 className="text-[var(--text)] font-bold text-lg mb-2">Cómo funciona internamente</h3>
                <p className="text-[var(--text-muted)] text-sm leading-relaxed">
                  El traductor combina tres tecnologías encadenadas en tiempo real: captura de audio del sistema operativo, transcripción con IA de Deepgram vía WebSocket, y traducción automática con Google Translate. Todo sucede en el browser, sin necesidad de un proxy de audio externo.
                </p>
              </div>

              {/* Pipeline Diagram */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center bg-[var(--surface)] border border-[var(--border)] p-4 rounded-xl text-center">
                <div className="flex flex-col items-center gap-1.5 p-3 bg-[var(--surface)] rounded-lg border border-[var(--border)]">
                  <Mic className="text-accent" size={24} />
                  <div className="text-xs font-bold text-[var(--text)]">Captura</div>
                  <div className="text-[11px] text-[var(--text-dim)] leading-normal">
                    Audio del sistema capturado con getDisplayMedia()
                  </div>
                </div>
                <div className="hidden md:block text-[var(--text-dim)] text-lg">➔</div>
                <div className="flex flex-col items-center gap-1.5 p-3 bg-[var(--surface)] rounded-lg border border-[var(--border)]">
                  <Zap className="text-accent" size={24} />
                  <div className="text-xs font-bold text-[var(--text)]">WebSocket</div>
                  <div className="text-[11px] text-[var(--text-dim)] leading-normal">
                    Chunks cada 250ms directos a Deepgram
                  </div>
                </div>
                <div className="hidden md:block text-[var(--text-dim)] text-lg">➔</div>
                <div className="flex flex-col items-center gap-1.5 p-3 bg-[var(--surface)] rounded-lg border border-[var(--border)]">
                  <Cpu className="text-accent" size={24} />
                  <div className="text-xs font-bold text-[var(--text)]">Nova-3 STT</div>
                  <div className="text-[11px] text-[var(--text-dim)] leading-normal">
                    Deepgram transcribe con smart_format activado
                  </div>
                </div>
              </div>

              {/* Pipeline Details */}
              <div className="flex flex-col gap-2.5">
                <h3 className="text-[var(--text-dim)] font-semibold text-xs uppercase tracking-wider font-mono mb-1">
                  Detalles del flujo
                </h3>
                <div className="flex justify-between py-2 border-b border-[var(--border)] text-[13px]">
                  <span className="text-[var(--text-dim)]">Protocolo de audio</span>
                  <span className="text-[var(--text)] font-medium">WebSocket (wss://) con token header</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[var(--border)] text-[13px]">
                  <span className="text-[var(--text-dim)]">Fragmentación</span>
                  <span className="text-[var(--text)] font-medium">Chunks de 250 ms — latencia ultra baja</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[var(--border)] text-[13px]">
                  <span className="text-[var(--text-dim)]">Mensajes recibidos</span>
                  <span className="text-[var(--text)] font-medium">Solo se procesan los de tipo is_final = true</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[var(--border)] text-[13px]">
                  <span className="text-[var(--text-dim)]">Traducción</span>
                  <span className="text-[var(--text)] font-medium">Google Translate API pública (en tiempo real)</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[var(--border)] text-[13px]">
                  <span className="text-[var(--text-dim)]">Document PiP</span>
                  <span className="text-[var(--text)] font-medium">Clonación de DOM a ventana flotante siempre visible</span>
                </div>
              </div>
            </div>
          )}

          {/* TAB: REQUISITOS */}
          {activeTab === "requisitos" && (
            <div className="flex flex-col gap-6">
              <div>
                <h3 className="text-[var(--text)] font-bold text-lg mb-2">Requisitos del sistema</h3>
                <p className="text-[var(--text-muted)] text-sm leading-relaxed">
                  El traductor corre de forma autónoma en el navegador, sin requerir instalaciones pesadas ni extensiones.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="p-5 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex flex-col gap-1.5">
                  <div className="text-sm font-bold text-[var(--text)]">Navegador Compatible</div>
                  <div className="text-[13px] text-[var(--text-muted)] leading-relaxed">
                    Chrome 116+ o Edge 116+. Obligatorio para soportar la captura combinada de audio del sistema y el Picture-in-Picture avanzado en tiempo real.
                  </div>
                </div>
                <div className="p-5 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex flex-col gap-1.5">
                  <div className="text-sm font-bold text-[var(--text)]">Audio del Sistema</div>
                  <div className="text-[13px] text-[var(--text-muted)] leading-relaxed">
                    Al presionar "Start", recuerda marcar la casilla de verificación <strong>"Compartir audio del sistema"</strong> en la ventana emergente al elegir la pestaña o pantalla que emite la voz.
                  </div>
                </div>
                <div className="p-5 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex flex-col gap-1.5">
                  <div className="text-sm font-bold text-[var(--text)]">Conexión de Red</div>
                  <div className="text-[13px] text-[var(--text-muted)] leading-relaxed">
                    Conexión a internet estable y continua. La transmisión de audio en vivo vía WebSocket hacia Deepgram requiere mantener un flujo sin cortes.
                  </div>
                </div>
                <div className="p-5 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex flex-col gap-1.5">
                  <div className="text-sm font-bold text-[var(--text)]">Conexión a Deepgram</div>
                  <div className="text-[13px] text-[var(--text-muted)] leading-relaxed">
                    Gestionada de forma 100% segura por el servidor backend de Kira (no necesitas generar ni configurar API Keys en tu navegador).
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB: COSTOS */}
          {activeTab === "costos" && (
            <div className="flex flex-col gap-5">
              <div>
                <h3 className="text-[var(--text)] font-bold text-lg mb-2">Créditos de Deepgram</h3>
                <p className="text-[var(--text-muted)] text-sm leading-relaxed">
                  Deepgram ofrece planes de prueba muy accesibles y un costo de operación extremadamente bajo comparado con otros motores de transcripción.
                </p>
              </div>

              <div className="p-4 bg-[var(--accent-muted)] border border-[var(--accent)]/20 text-[var(--accent)] rounded-xl text-[13px] leading-relaxed flex items-center gap-3">
                <Gift size={18} className="flex-shrink-0" />
                <span>Al crear una cuenta en Deepgram, se otorgan <strong>$200 USD en créditos gratuitos</strong> de un solo uso sin necesidad de tarjeta. Estos créditos permiten probar la API de inmediato.</span>
              </div>

              <h4 className="text-xs font-bold uppercase tracking-wider font-mono text-[var(--text-dim)] mt-2">
                Duración Estimada de los Créditos
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-[var(--surface)] border border-[var(--accent)]/30 rounded-lg relative overflow-hidden">
                  <div className="absolute top-2 right-2 bg-[var(--accent-muted)] text-[var(--accent)] text-[8px] font-bold uppercase px-1.5 py-0.5 rounded">
                    Nova-3 (Usado aquí)
                  </div>
                  <div className="text-xs font-semibold text-[var(--text-muted)]">Nova-3 Streaming</div>
                  <div className="text-2xl font-bold text-[var(--text)] my-1">~574 horas</div>
                  <div className="text-[10px] text-[var(--text-dim)] leading-relaxed">
                    Tarifa base: $0.0058 / min (~34,480 min). Con Smart Format: $0.0077 / min (~432 hrs).
                  </div>
                </div>

                <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-lg">
                  <div className="text-xs font-semibold text-[var(--text-muted)]">Nova-2 (Anterior)</div>
                  <div className="text-2xl font-bold text-[var(--text)]/60 my-1">~775 horas</div>
                  <div className="text-[10px] text-[var(--text-dim)] leading-relaxed">
                    Tarifa base: $0.0043 / minuto. Equivale a 46,500 minutos.
                  </div>
                </div>
              </div>
              <p className="text-xs text-[var(--text-dim)] italic">
                * Precios de referencia de Deepgram. La facturación es al segundo y no por minuto redondeado, optimizando aún más el consumo de saldo.
              </p>
            </div>
          )}

          {/* TAB: TIPS */}
          {activeTab === "tips" && (
            <div className="flex flex-col gap-3.5">
              <div>
                <h3 className="text-[var(--text)] font-bold text-lg mb-0.5">Tips para obtener el mejor resultado</h3>
                <p className="text-[var(--text-muted)] text-xs">
                  Recomendaciones clave para lograr una transcripción y traducción precisas en todas tus reuniones.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                <div className="p-3.5 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex flex-col gap-1 min-w-0">
                  <div className="text-sm font-bold text-[var(--text)]">Compartir audio de pestaña específica</div>
                  <div className="text-xs text-[var(--text-muted)] leading-normal">
                    Para capturar audio de reuniones en Google Meet, Zoom Web o videos de YouTube, selecciona específicamente la pestaña del navegador en lugar de la pantalla completa para garantizar una calidad digital directa y sin interferencias.
                  </div>
                </div>

                <div className="p-3.5 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex flex-col gap-1 min-w-0">
                  <div className="text-sm font-bold text-[var(--text)]">Uso recomendado de auriculares</div>
                  <div className="text-xs text-[var(--text-muted)] leading-normal">
                    Si estás hablando por el micrófono mientras capturas una reunión, utiliza auriculares. De esta forma evitarás que la voz de los demás participantes salga por los altavoces y se vuelva a colar por tu micrófono generando eco o duplicación.
                  </div>
                </div>

                <div className="p-3.5 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex flex-col gap-1 min-w-0">
                  <div className="text-sm font-bold text-[var(--text)]">Configuración del Idioma de Origen</div>
                  <div className="text-xs text-[var(--text-muted)] leading-normal">
                    Utiliza la opción <strong>"Auto-detectar"</strong> solo si habrá cambios frecuentes de idioma entre los hablantes. Si la reunión será íntegramente en un idioma (ej. Inglés o Español), seleccionarlo de forma explícita aumentará la precisión de los nombres técnicos.
                  </div>
                </div>

                <div className="p-3.5 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex flex-col gap-1 min-w-0">
                  <div className="text-sm font-bold text-[var(--text)]">Prioridad de procesamiento y ventana activa</div>
                  <div className="text-xs text-[var(--text-muted)] leading-normal">
                    No minimices la ventana principal o la ventana flotante PiP si cambias a otras aplicaciones de escritorio. Mantenerlas visibles asegura que el navegador preserve máxima prioridad para el procesamiento de audio en segundo plano.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB: MINUTAS */}
          {activeTab === "minutas" && (
            <div className="flex flex-col gap-3.5">
              <div>
                <h3 className="text-[var(--text)] font-bold text-lg mb-0.5">Minutas automáticas con IA</h3>
                <p className="text-[var(--text-muted)] text-xs">
                  Kira te permite estructurar minutas ejecutivas de reunión automáticamente enviando el historial cronológico a un modelo LLM avanzado.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                <div className="p-3.5 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex items-start gap-3">
                  <div className="w-6 h-6 rounded-md bg-[var(--accent)]/15 border border-[var(--accent)]/30 text-[var(--accent)] font-mono font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                    1
                  </div>
                  <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    <div className="text-sm font-bold text-[var(--text)]">Generación Inteligente</div>
                    <div className="text-xs text-[var(--text-muted)] leading-normal">
                      Al finalizar o detener una sesión, puedes generar tu reporte presionado el botón <strong>"Minuta"</strong>. El sistema agrupa los segmentos de audio y los envía estructurados cronológicamente.
                    </div>
                  </div>
                </div>

                <div className="p-3.5 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex items-start gap-3">
                  <div className="w-6 h-6 rounded-md bg-[var(--accent)]/15 border border-[var(--accent)]/30 text-[var(--accent)] font-mono font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                    2
                  </div>
                  <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    <div className="text-sm font-bold text-[var(--text)]">Formateado de Acuerdos</div>
                    <div className="text-xs text-[var(--text-muted)] leading-normal">
                      El modelo de IA procesa la conversación y genera una minuta formal que clasifica automáticamente los temas clave discutidos, las decisiones tomadas y las tareas asignadas a cada responsable.
                    </div>
                  </div>
                </div>

                <div className="p-3.5 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex items-start gap-3">
                  <div className="w-6 h-6 rounded-md bg-[var(--accent)]/15 border border-[var(--accent)]/30 text-[var(--accent)] font-mono font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                    3
                  </div>
                  <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    <div className="text-sm font-bold text-[var(--text)]">Exportación Múltiple</div>
                    <div className="text-xs text-[var(--text-muted)] leading-normal">
                      Puedes descargar la minuta instantáneamente en formato plano Markdown (<code>.md</code>) o en un archivo interactivo y autocontenido HTML (<code>.html</code>) estructurado con el diseño de macOS.
                    </div>
                  </div>
                </div>

                <div className="p-3.5 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex items-start gap-3">
                  <div className="w-6 h-6 rounded-md bg-[var(--accent)]/15 border border-[var(--accent)]/30 text-[var(--accent)] font-mono font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                    4
                  </div>
                  <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    <div className="text-sm font-bold text-[var(--text)]">Envío Directo por Correo</div>
                    <div className="text-xs text-[var(--text-muted)] leading-normal">
                      Desde la misma ventana puedes ingresar múltiples direcciones de correo electrónico separadas por comas o punto y coma para enviarles una copia idéntica del reporte procesado.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB: ATAJOS */}
          {activeTab === "atajos" && (
            <div className="flex flex-col gap-5">
              <div>
                <h3 className="text-[var(--text)] font-bold text-lg mb-1 flex items-center gap-2">
                  <Keyboard size={20} className="text-[var(--accent)]" />
                  <span>Atajos de Teclado Globales</span>
                </h3>
                <p className="text-[var(--text-muted)] text-sm leading-relaxed">
                  Puedes utilizar estos comandos rápidos en cualquier momento para agilizar tu flujo de trabajo sin necesidad de usar el mouse.
                </p>
              </div>

              <div className="flex flex-col gap-3.5">
                <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex items-center justify-between gap-4">
                  <div className="flex flex-col gap-1 min-w-0 flex-1">
                    <div className="text-sm font-bold text-[var(--text)]">Iniciar / Detener Grabación</div>
                    <div className="text-[13px] text-[var(--text-dim)] leading-relaxed">
                      Alterna la captura de audio en la pantalla principal. Al detener una sesión activa, abrirá el cuadro de confirmación para evitar cortes accidentales.
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <kbd className="px-3 py-1.5 text-sm font-mono font-bold bg-[var(--window-bg)] border border-[var(--border)] rounded-md text-[var(--text)] shadow-sm">
                      Espacio
                    </kbd>
                  </div>
                </div>

                <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex items-center justify-between gap-4">
                  <div className="flex flex-col gap-1 min-w-0 flex-1">
                    <div className="text-sm font-bold text-[var(--text)]">Alternar Modo de Aplicación</div>
                    <div className="text-[13px] text-[var(--text-dim)] leading-relaxed">
                      Cambia al instante entre el modo <strong>"Traductor + Minuta"</strong> y el modo <strong>"Solo Minuta"</strong> con un solo atajo, sin tener que hacer clic en el selector del header.
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <kbd className="px-3 py-1.5 text-sm font-mono font-bold bg-[var(--window-bg)] border border-[var(--border)] rounded-md text-[var(--text)] shadow-sm">
                      Ctrl
                    </kbd>
                    <span className="text-[var(--text-dim)] text-xs">+</span>
                    <kbd className="px-3 py-1.5 text-sm font-mono font-bold bg-[var(--window-bg)] border border-[var(--border)] rounded-md text-[var(--text)] shadow-sm">
                      M
                    </kbd>
                  </div>
                </div>

                <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl flex items-center justify-between gap-4">
                  <div className="flex flex-col gap-1 min-w-0 flex-1">
                    <div className="text-sm font-bold text-[var(--text)]">Cerrar Modales Activos</div>
                    <div className="text-[13px] text-[var(--text-dim)] leading-relaxed">
                      Cierra de forma rápida cualquier ventana flotante o modal abierto en pantalla (Documentación, Historial, Minuta, Configuración, Perfil, etc.).
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <kbd className="px-3 py-1.5 text-sm font-mono font-bold bg-[var(--window-bg)] border border-[var(--border)] rounded-md text-[var(--text)] shadow-sm">
                      Esc
                    </kbd>
                  </div>
                </div>
              </div>

              <p className="text-xs text-[var(--text-dim)] italic mt-1">
                Nota: Los atajos se desactivan automáticamente cuando tu cursor se encuentra escribiendo dentro de un campo de texto (como la búsqueda de grabaciones o el input de correo) para no interrumpir tu escritura.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
